// Thin API client. Same-origin fetch so the host-only session cookie rides along
// automatically — no token handling in JS. All calls are relative to the tenant
// host the SPA is served from.

// A single global "no longer authenticated" hook. The app registers it so that when
// any call 401s — e.g. this device was signed out by a single-seat eviction after
// the account signed in elsewhere — it re-validates and falls back to the sign-in
// screen instead of silently failing.
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) { onUnauthorized = fn; }

// '?a=1&b=x' from a params object, empty values dropped ('' when nothing is left).
function kbQuery(p) {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(p || {}).filter(([, v]) => v != null && v !== '' && v !== false))).toString();
  return q ? `?${q}` : '';
}

// `priority: 'low'` (opt-in) is for background traffic — the presence heartbeat, the unread
// badge poll, the scan poll — so it yields to the request the user is waiting on (their thread,
// the list). Fetch Priority; engines without it ignore the option.
async function req(method, path, body, { priority } = {}) {
  const opts = {
    method,
    credentials: 'same-origin',
    headers: {}
  };
  if (priority) opts.priority = priority;
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(path, opts);
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    // Auth lost mid-session (e.g. single-seat eviction): let the app re-validate and
    // drop to sign-in. /api/me itself never 401s (it returns nulls), so this can't loop.
    if (res.status === 401 && onUnauthorized) { try { onUnauthorized(); } catch { /* ignore */ } }
    const err = new Error(data?.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  get: (p) => req('GET', p),
  post: (p, b) => req('POST', p, b),
  patch: (p, b) => req('PATCH', p, b),

  // Auth / bootstrap
  me: () => req('GET', '/api/me'),
  logout: () => req('POST', '/auth/logout'),
  agentLogout: () => req('POST', '/auth/agent/logout'),
  // Identity switch (same email = agent + customer in this workspace). switchToCustomer
  // drops the agent session and returns the portal URL; requestAgentSwitch emails the
  // agent sign-in link (2FA still required) and only ever returns { sent: true }.
  switchToCustomer: () => req('POST', '/api/switch/customer'),
  requestAgentSwitch: () => req('POST', '/api/switch/agent/request'),
  // Passkey switch (customer → agent, one tap) — the scoped ceremony; options is gated by
  // /api/me.alsoAgentPasskey, verify returns { url } and drops the customer session.
  switchAgentPasskeyOptions: () => req('POST', '/api/switch/agent/passkey/options'),
  switchAgentPasskeyVerify: (body) => req('POST', '/api/switch/agent/passkey/verify', body),
  // Unified sign-in — server routes agents vs customers by email.
  requestSignIn: (email) => req('POST', '/auth/signin/request', { email }),

  // 2FA — sign-in second factor (after the magic link) + agent-self management.
  mfaVerify: (body) => req('POST', '/auth/agent/2fa/verify', body),
  mfaStartTotp: () => req('POST', '/api/mfa/totp/start'),
  mfaConfirmTotp: (code) => req('POST', '/api/mfa/totp/confirm', { code }),
  // Re-auth-gated actions take a reauth object: { code } (TOTP/recovery) OR { assertion } (passkey).
  mfaDisableTotp: (reauth) => req('POST', '/api/mfa/totp/disable', reauth),
  mfaRegenerateRecovery: (reauth) => req('POST', '/api/mfa/recovery-codes', reauth),
  mfaReauthPasskeyOptions: () => req('POST', '/api/mfa/reauth/passkey/options'),
  // Passkeys (WebAuthn)
  mfaPasskeyRegisterOptions: () => req('POST', '/api/mfa/passkey/register/options'),
  mfaPasskeyRegisterVerify: (body) => req('POST', '/api/mfa/passkey/register/verify', body),
  mfaPasskeyAuthOptions: () => req('POST', '/auth/agent/2fa/passkey/options'),
  mfaListPasskeys: () => req('GET', '/api/mfa/passkeys'),
  mfaRemovePasskey: (credentialId, reauth) => req('POST', '/api/mfa/passkey/remove', { credentialId, ...reauth }),
  // First-factor passkey sign-in (WebAuthn — skips the magic link).
  passkeySignInOptions: () => req('POST', '/auth/passkey/options'),
  passkeySignInVerify: (body) => req('POST', '/auth/passkey/verify', body),
  // Customer passkeys (portal self-service — no second factor, so no re-auth code).
  myPasskeyRegisterOptions: () => req('POST', '/api/my/passkeys/register/options'),
  myPasskeyRegisterVerify: (body) => req('POST', '/api/my/passkeys/register/verify', body),
  myListPasskeys: () => req('GET', '/api/my/passkeys'),
  myRemovePasskey: (credentialId) => req('POST', '/api/my/passkeys/remove', { credentialId }),
  // 2FA policy + reset. adminPutSecurityPolicy takes a partial { requireMfa?, requirePasskey? }.
  adminSecurityPolicy: () => req('GET', '/api/admin/security-policy'),
  adminPutSecurityPolicy: (body) => req('PUT', '/api/admin/security-policy', body),
  adminResetAgentMfa: (id, code) => req('POST', `/api/admin/agents/${id}/reset-2fa`, code ? { code } : undefined),
  platformResetAgentMfa: (id) => req('POST', `/api/platform/users/agents/${id}/reset-2fa`),
  platformResetEndUserPasskeys: (id) => req('POST', `/api/platform/users/end-users/${id}/reset-passkeys`),

  // Self-serve signup (served only on app.<rootDomain>). Live slug availability +
  // the verify-first request; the workspace is created when the emailed link is
  // confirmed, not here.
  slugAvailable: (slug) => req('GET', `/api/signup/slug-available?slug=${encodeURIComponent(slug)}`),
  signup: (body) => req('POST', '/api/signup', body),
  // Workspace recall ("forgot which workspace?") — emails the list to the address.
  // Response is always generic (never says whether anything matched).
  forgotWorkspaces: (body) => req('POST', '/api/forgot-workspaces', body),

  // Platform admin console (served only on admin.<rootDomain>). Passwordless
  // sign-in mirrors the tenant flow; the cross-tenant /api/platform/* calls cover
  // insights, workspace management, user management, and the operator audit log.
  requestAdminSignIn: (email) => req('POST', '/auth/admin/request', { email }),
  adminLogout: () => req('POST', '/auth/admin/logout'),
  platformInsights: (days = 30) => req('GET', `/api/platform/insights?days=${encodeURIComponent(days)}`),
  platformRevenue: (months = 12) => req('GET', `/api/platform/revenue?months=${encodeURIComponent(months)}`),
  platformWorkspaces: (q = '', status = '') => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    const qs = p.toString();
    return req('GET', `/api/platform/workspaces${qs ? `?${qs}` : ''}`);
  },
  platformWorkspace: (id) => req('GET', `/api/platform/workspaces/${id}`),
  // One workspace from a subdomain, its slug host, or a verified custom domain (404 = none).
  platformResolveWorkspace: (q) => req('GET', `/api/platform/workspaces/resolve?q=${encodeURIComponent(q)}`),
  // Read-only, audited ticket viewing (opening a ticket writes an audit row).
  platformWorkspaceTickets: (id, { status = '', q = '' } = {}) => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (q) p.set('q', q);
    const qs = p.toString();
    return req('GET', `/api/platform/workspaces/${id}/tickets${qs ? `?${qs}` : ''}`);
  },
  platformWorkspaceTicket: (id, ticketId) => req('GET', `/api/platform/workspaces/${id}/tickets/${ticketId}`),
  platformWorkspaceBilling: (id) => req('GET', `/api/platform/workspaces/${id}/billing`),
  platformBillingCoupons: () => req('GET', '/api/platform/billing/coupons'),
  platformApplyCoupon: (id, couponId) => req('POST', `/api/platform/workspaces/${id}/billing/coupon`, { couponId }),
  platformRemoveCoupon: (id) => req('DELETE', `/api/platform/workspaces/${id}/billing/coupon`),
  platformRefund: (id, body) => req('POST', `/api/platform/workspaces/${id}/billing/refund`, body),
  platformSuspendWorkspace: (id, reason) => req('POST', `/api/platform/workspaces/${id}/suspend`, { reason }),
  platformUnsuspendWorkspace: (id) => req('POST', `/api/platform/workspaces/${id}/unsuspend`),
  platformDeleteWorkspace: (id, confirm) => req('DELETE', `/api/platform/workspaces/${id}`, { confirm }),
  platformUsers: (q = '', type = 'all') => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (type && type !== 'all') p.set('type', type);
    const qs = p.toString();
    return req('GET', `/api/platform/users${qs ? `?${qs}` : ''}`);
  },
  platformAgent: (id) => req('GET', `/api/platform/users/agents/${id}`),
  platformEndUser: (id) => req('GET', `/api/platform/users/end-users/${id}`),
  platformSetAgentStatus: (id, status) => req('PATCH', `/api/platform/users/agents/${id}`, { status }),
  platformSetEndUserStatus: (id, status) => req('PATCH', `/api/platform/users/end-users/${id}`, { status }),
  platformRevokeAgentSessions: (id) => req('POST', `/api/platform/users/agents/${id}/revoke-sessions`),
  platformRevokeEndUserSessions: (id) => req('POST', `/api/platform/users/end-users/${id}/revoke-sessions`),
  platformAudit: (limit = 50) => req('GET', `/api/platform/audit?limit=${encodeURIComponent(limit)}`),
  platformSlugRules: () => req('GET', '/api/platform/slug-rules'),
  platformCreateSlugRule: (body) => req('POST', '/api/platform/slug-rules', body),
  platformDeleteSlugRule: (id) => req('DELETE', `/api/platform/slug-rules/${id}`),
  platformReassignSubdomain: (id, slug) => req('POST', `/api/platform/workspaces/${id}/subdomain`, { slug }),
  // Un-reserve a previous subdomain: its old host stops redirecting; the slug is claimable.
  platformReleasePreviousSlug: (id, slug) => req('DELETE', `/api/platform/workspaces/${id}/previous-slugs/${encodeURIComponent(slug)}`),
  // Operator 2FA: sign-in second factor (after the magic link) + operator-self management.
  adminMfaVerify: (body) => req('POST', '/auth/admin/2fa/verify', body),
  platformMfaStatus: () => req('GET', '/api/platform/mfa/status'),
  platformMfaStartTotp: () => req('POST', '/api/platform/mfa/totp/start'),
  platformMfaConfirmTotp: (code) => req('POST', '/api/platform/mfa/totp/confirm', { code }),
  platformMfaDisableTotp: (code) => req('POST', '/api/platform/mfa/totp/disable', { code }),
  platformMfaRotateStart: (code) => req('POST', '/api/platform/mfa/totp/rotate/start', { code }),
  platformMfaRotateConfirm: (code) => req('POST', '/api/platform/mfa/totp/rotate/confirm', { code }),
  platformMfaRegenerateRecovery: (code) => req('POST', '/api/platform/mfa/recovery-codes', { code }),
  // Peer operators (list + reset another operator's 2FA)
  platformOperators: () => req('GET', '/api/platform/operators'),
  platformResetOperatorMfa: (email, code) => req('POST', '/api/platform/operators/reset-2fa', { email, code }),
  // Operator passkeys (WebAuthn on admin.<root>)
  platformMfaPasskeyRegisterOptions: () => req('POST', '/api/platform/mfa/passkey/register/options'),
  platformMfaPasskeyRegisterVerify: (body) => req('POST', '/api/platform/mfa/passkey/register/verify', body),
  platformMfaPasskeyAuthOptions: () => req('POST', '/auth/admin/2fa/passkey/options'),
  platformMfaListPasskeys: () => req('GET', '/api/platform/mfa/passkeys'),
  platformMfaRemovePasskey: (credentialId, code) => req('POST', '/api/platform/mfa/passkey/remove', { credentialId, code }),
  // First-factor operator passkey sign-in (skips the magic link).
  adminPasskeySignInOptions: () => req('POST', '/auth/admin/passkey/options'),
  adminPasskeySignInVerify: (body) => req('POST', '/auth/admin/passkey/verify', body),

  // Select-menu data
  listAgents: () => req('GET', '/api/agents'),
  listTypes: () => req('GET', '/api/ticket-types'),

  // Tickets — agent
  listTickets: (query = '') => req('GET', `/api/tickets${query ? `?${query}` : ''}`),
  getTicket: (id, opts) => req('GET', `/api/tickets/${id}`, undefined, opts),
  // Speculative, SIDE-EFFECT-FREE read (no view tracking, no throttle reset) used to warm
  // the thread + its image thumbnails from the list before a click. See lib/ticketPrefetch.js.
  prefetchTicket: (id) => req('GET', `/api/tickets/${id}?prefetch=1`),
  reply: (id, body) => req('POST', `/api/tickets/${id}/reply`, body),
  // An agent opens a ticket FOR a customer (the console's New ticket). Body: { endUserId } or
  // { email, name? }, subject, text, html, typeId?, priority, assignedAgentId|null, emailCustomer.
  // → { ticket, message, emailed, emailSkipped }
  agentCreateTicket: (body) => req('POST', '/api/tickets/outbound', body),
  // The New-ticket customer picker: → { customers: [{ id, name, email, externalId, status, language, avatarUrl }] }
  searchCustomers: (q) => req('GET', `/api/customers?q=${encodeURIComponent(q)}`),
  snoozeTicket: (id, fallbackUntil) => req('POST', `/api/tickets/${id}/snooze`, { fallbackUntil }),
  resendCustomerEmail: (id) => req('POST', `/api/tickets/${encodeURIComponent(id)}/resend-email`),
  scheduleReply: (id, body) => req('POST', `/api/tickets/${id}/scheduled-reply`, body),
  updateScheduledReply: (id, sendAt) => req('PATCH', `/api/scheduled-replies/${id}`, { sendAt }),
  deleteScheduledReply: (id) => req('DELETE', `/api/scheduled-replies/${id}`),
  sendScheduledReplyNow: (id) => req('POST', `/api/scheduled-replies/${id}/send-now`),
  note: (id, body) => req('POST', `/api/tickets/${id}/note`, body),
  patchTicket: (id, changes) => req('PATCH', `/api/tickets/${id}`, changes),
  // Manual customer verification: link (or unlink, with '') the ticket customer's
  // account id in the workspace's own service.
  linkTicketCustomer: (id, externalId) => req('POST', `/api/tickets/${id}/customer/link`, { externalId }),

  summarize: (id) => req('POST', `/api/tickets/${id}/summarize`),
  generateReply: (id) => req('POST', `/api/tickets/${id}/generate-reply`),
  translateMessage: (id, target) => req('POST', `/api/messages/${id}/translate`, target ? { target } : {}),
  translatePreview: (id, body) => req('POST', `/api/tickets/${id}/translate-preview`, body),
  // Presence heartbeat — "I'm looking at this ticket" (suppresses the
  // customer-reply notification for the agent who's already viewing it).
  pingViewingTicket: (id) => req('POST', `/api/tickets/${id}/viewing`, undefined, { priority: 'low' }),

  // Attachment upload — multipart, so it can't go through `req` (which always
  // JSON-encodes). No Content-Type header: the browser sets the boundary.
  uploadAttachment: async (ticketId, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const res = await fetch(`/api/tickets/${ticketId}/attachments`, {
      method: 'POST', credentials: 'same-origin', body: fd
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.message || `Upload failed (${res.status})`);
    return data;
  },
  // Same-origin serve URL (302s to a short-lived presigned R2 URL).
  attachmentUrl: (id, { download, variant } = {}) => `/api/attachments/${id}${download ? '?download=1' : variant ? `?variant=${variant}` : ''}`,
  rescanAttachment: (id) => req('POST', `/api/attachments/${id}/rescan`),

  // Tickets — customer
  listMyTickets: (status) =>
    req('GET', `/api/my/tickets${status ? `?status=${status}` : ''}`),
  // Customer self-service profile: name, avatarUrl, prefs.theme, language. Each
  // field is optional; an explicit null clears it (language null → back to detection).
  updateMyProfile: (patch) => req('PATCH', '/api/my/profile', patch),
  resumeMyEmail: () => req('POST', '/api/my/email/resume'),
  deleteMyAccount: () => req('POST', '/api/my/account/delete', { confirm: true }),
  muteNotifications: () => req('POST', '/api/my/notifications/mute'),
  unmuteNotifications: () => req('POST', '/api/my/notifications/unmute'),
  customerReply: (id, body) => req('POST', `/api/tickets/${id}/message`, body),
  // `afterAutoAnswer` marks a self-close made straight from the AI answer's CTA (deflection metric).
  customerSetStatus: (id, status, { afterAutoAnswer = false } = {}) =>
    req('PATCH', `/api/my/tickets/${id}`, { status, ...(afterAutoAnswer ? { afterAutoAnswer: true } : {}) }),
  // "No, keep it open" under the AI answer: hides its buttons for good (no status change).
  customerDismissAiAnswer: (id) => req('POST', `/api/my/tickets/${encodeURIComponent(id)}/ai-answer/dismiss`),
  createTicket: (body) => req('POST', '/api/tickets', body),

  // Templates (agents)
  templates: (q) => req('GET', `/api/templates${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  createTemplateItem: (body) => req('POST', '/api/templates', body),
  updateTemplateItem: (id, body) => req('PATCH', `/api/templates/${id}`, body),
  deleteTemplateItem: (id) => req('DELETE', `/api/templates/${id}`),

  // Notifications (agents)
  notifications: (opts) => req('GET', '/api/notifications', undefined, opts),
  markNotificationsRead: () => req('POST', '/api/notifications/read'),
  clearNotification: (id) => req('DELETE', `/api/notifications/${id}`),
  clearAllNotifications: () => req('DELETE', '/api/notifications'),

  // Profile (agents)
  updateProfile: (body) => req('PATCH', '/api/profile', body),
  pushStatus: (endpoint) => req('GET', `/api/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`),
  pushSubscribe: (body) => req('POST', '/api/push/subscribe', body),
  pushUnsubscribe: (endpoint) => req('DELETE', '/api/push/subscribe', { endpoint }),

  // Account (owner) — name change reuses updateProfile({ fullName }).
  requestEmailChange: (newEmail) => req('POST', '/api/account/email/request', { newEmail }),
  resetContent: () => req('POST', '/api/account/reset'),
  deleteAccount: () => req('POST', '/api/account/delete'),
  // Content export — a zip download, so it bypasses `req` (which JSON-parses).
  // Reads the server-provided filename and triggers a browser save.
  downloadExport: async () => {
    const res = await fetch('/api/account/export', { credentials: 'same-origin' });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let message;
      try { message = JSON.parse(text)?.message; } catch { message = null; }
      throw new Error(message || `Export failed (${res.status})`);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m ? m[1] : 'essential-support-export.zip';
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Admin (owner)
  adminAgents: () => req('GET', '/api/admin/agents'),
  adminCreateAgent: (body) => req('POST', '/api/admin/agents', body),
  adminUpdateAgent: (id, body) => req('PATCH', `/api/admin/agents/${id}`, body),
  adminDeleteAgent: (id, reassignTo) =>
    req('DELETE', `/api/admin/agents/${id}${reassignTo && reassignTo !== 'none' ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ''}`),
  adminTypes: () => req('GET', '/api/admin/types'),
  adminCreateType: (body) => req('POST', '/api/admin/types', body),
  adminUpdateType: (id, body) => req('PATCH', `/api/admin/types/${id}`, body),
  adminDeleteType: (id, reassignTo) =>
    req('DELETE', `/api/admin/types/${id}${reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ''}`),
  adminReorderTypes: (ids) => req('PUT', '/api/admin/types/order', { ids }),
  adminRouting: () => req('GET', '/api/admin/routing'),
  adminPutRouting: (routing) => req('PUT', '/api/admin/routing', { routing }),
  adminAutoClose: () => req('GET', '/api/admin/auto-close'),
  adminPutAutoClose: (body) => req('PUT', '/api/admin/auto-close', body),
  adminAutoReply: () => req('GET', '/api/admin/auto-reply'),
  adminPutAutoReply: (body) => req('PUT', '/api/admin/auto-reply', body),
  adminAvailability: () => req('GET', '/api/admin/availability'),
  adminPutAvailability: (body) => req('PUT', '/api/admin/availability', body),
  adminBranding: () => req('GET', '/api/admin/branding'),
  adminPutBranding: (body) => req('PUT', '/api/admin/branding', body),
  adminPutBrandingLogo: (dataUrl) => req('PUT', '/api/admin/branding/logo', { dataUrl }),
  adminDeleteBrandingLogo: () => req('DELETE', '/api/admin/branding/logo'),
  // App icons (favicon + PWA), kind ∈ favicon | appleTouch | maskable
  adminPutBrandingIcon: (kind, dataUrl) => req('PUT', `/api/admin/branding/icon/${kind}`, { dataUrl }),
  adminDeleteBrandingIcon: (kind) => req('DELETE', `/api/admin/branding/icon/${kind}`),
  adminConnections: () => req('GET', '/api/admin/connections'),
  adminPutConnections: (body) => req('PUT', '/api/admin/connections', body),
  adminTestConnection: (provider) => req('POST', `/api/admin/connections/${provider}/test`),
  // The model dropdown for one AI provider: { models: [{ id, label, older? }], selected, upgradedFrom? }
  adminAiModels: (provider) => req('GET', `/api/admin/ai/models?provider=${encodeURIComponent(provider)}`),
  // Instant answers (AI auto-answer / deflection) knowledge base
  adminKb: () => req('GET', '/api/admin/kb'),
  adminPutKb: (body) => req('PUT', '/api/admin/kb', body),
  adminDeleteKb: () => req('DELETE', '/api/admin/kb'),
  adminTestKb: (question) => req('POST', '/api/admin/kb/test', { question }),
  // Hosted Knowledge Base settings (owner). GET → { enabled, name, language, languages,
  // publishedByLanguage, aiDescriptions, aiGuidance, editAccess{mode,agentIds}, byline{mode,name},
  // rev, counts{…}, max }; PUT takes any partial of those fields (P7: `languages` = the FULL
  // enabled set of additional languages, `language` = the default) and answers with the same
  // payload.
  adminKbSettings: () => req('GET', '/api/admin/kb-settings'),
  adminPutKbSettings: (body) => req('PUT', '/api/admin/kb-settings', body),
  // Hosted Knowledge Base — agent authoring (server/src/kb). Work while the KB is off.
  // Writes take { ifVersion } and answer the article with its new `version`; a stale one
  // 409s with err.data.version. redirectTo = { articleId } | { categoryId } | { url } | null.
  // p (P7): { language?: a locale (default: the default language), all?: true (every edition) }.
  kbTree: (p = {}) => req('GET', `/api/kb/tree${kbQuery(p)}`),
  // b: { name, slug?, description?, icon?, parentId?, language?, translationOf? (P7) }.
  kbCreateCategory: (b) => req('POST', '/api/kb/categories', b),
  kbUpdateCategory: (id, b) => req('PATCH', `/api/kb/categories/${id}`, b),
  // parentId null/'' = the top level.
  kbReorderCategories: (parentId, ids) => req('PUT', `/api/kb/categories/${parentId || 'root'}/order`, { ids }),
  kbDeleteCategory: (id, { moveTo, redirectTo } = {}) =>
    req('DELETE', `/api/kb/categories/${id}${moveTo ? `?moveTo=${encodeURIComponent(moveTo)}` : ''}`, { redirectTo: redirectTo ?? null }),
  // p: { status, category (a category id), q, cursor, limit, sort, tag, language (P7: a
  // locale | 'all') } — empty values are dropped. → { articles, nextCursor, total }. sort ∈
  // updated | published | title | order.
  kbArticles: (p = {}) =>
    req('GET', `/api/kb/articles?${new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v != null && v !== '')))}`),
  kbArticle: (id) => req('GET', `/api/kb/articles/${id}`),
  // b: { title, categoryId, slug?, lexical?, description?, tags?, language? (P7: must be the category's) }.
  kbCreateArticle: (b) => req('POST', '/api/kb/articles', b),
  // ── Translations (docs/kb-specs/P7-SPEC.md §3.2). b: { language, mode: 'ai'|'copy'|'blank' }
  // → 201 { article } (the new DRAFT edition); 409 { reason:'exists'|'trashed', articleId }.
  kbCreateTranslation: (id, b) => req('POST', `/api/kb/articles/${id}/translations`, b),
  // A translation row only → 202 { queued: true } (409 while a job is pending).
  kbRetranslate: (id) => req('POST', `/api/kb/articles/${id}/translation/retranslate`),
  // Mark a translation up to date with its source → { article }.
  kbAckTranslation: (id) => req('POST', `/api/kb/articles/${id}/translation/ack`),
  // Cancel a pending (unleased) AI translation job → { article }.
  kbCancelTranslation: (id) => req('POST', `/api/kb/articles/${id}/translation/cancel`),
  // { ifVersion, title?, lexical?, slug?, categoryId?, description?, tags?, discardDraft? }
  kbSaveArticle: (id, b) => req('PATCH', `/api/kb/articles/${id}`, b),
  // action ∈ publish | unpublish | archive | restore | revert | move
  kbArticleAction: (id, action, b = {}) => req('POST', `/api/kb/articles/${id}/${action}`, b),
  kbDeleteArticle: (id, { redirectTo } = {}) => req('DELETE', `/api/kb/articles/${id}`, { redirectTo: redirectTo ?? null }),
  kbRevisions: (id) => req('GET', `/api/kb/articles/${id}/revisions`),
  // ── Management polish (docs/kb-specs/P5-SPEC.md §3). kbArticles also takes sort:'order'
  // (manual order — needs `category`) and `tag` (case-insensitive exact); kbUpdateCategory
  // takes `position` (index among the (new) parent's children).
  // Move one article to `position` within its category → { ok, order: [ids] }.
  kbReorderArticle: (categoryId, id, position) => req('PUT', `/api/kb/categories/${categoryId}/articles/order`, { id, position }),
  // action ∈ KB_BULK_ACTIONS; params { categoryId?, tags?, redirectTo? } →
  // { results: [{ id, ok, unchanged?, code?, message? }], summary: { ok, failed, unchanged } }.
  kbBulk: (action, ids, params = {}) => req('POST', '/api/kb/bulk', { action, ids, params }),
  // Redirects (admin/owner). GET → { redirects: [{ id, fromPath, fromHref, toKind, toId, toUrl,
  // toHref, toTitle, source, removedId, createdAt }] }; create body { fromPath, to }.
  kbRedirects: () => req('GET', '/api/kb/redirects'),
  kbCreateRedirect: (b) => req('POST', '/api/kb/redirects', b),
  kbDeleteRedirect: (id) => req('DELETE', `/api/kb/redirects/${id}`),
  // Imported (legacy) snippets. p: { cursor, limit } → { snippets, nextCursor, total }.
  kbSnippets: (p = {}) =>
    req('GET', `/api/kb/snippets?${new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v != null && v !== '')))}`),
  kbConvertSnippet: (id, categoryId) => req('POST', `/api/kb/snippets/${id}/convert`, { categoryId }),
  kbConvertSnippets: (ids, categoryId) => req('POST', '/api/kb/snippets/convert', { ids, categoryId }),
  kbDeleteSnippet: (id) => req('DELETE', `/api/kb/snippets/${id}`),
  // Tags → { tags: [{ name, slug, published, drafts, href }] }; rename → { ok, articles, merged }.
  // P7: `language` (a locale) = one edition's tags / the edition a rename or remove acts on;
  // absent = every edition (P6).
  kbTags: (p = {}) => req('GET', `/api/kb/tags${kbQuery(p)}`),
  kbRenameTag: (from, to, language = null) => req('POST', '/api/kb/tags/rename', { from, to, ...(language ? { language } : {}) }),
  kbRemoveTag: (tag, language = null) => req('POST', '/api/kb/tags/remove', { tag, ...(language ? { language } : {}) }),
  // One revision WITH its lexical body (the Compare dialog) → { revision }.
  kbRevision: (id, rev) => req('GET', `/api/kb/articles/${id}/revisions/${rev}`),
  // Link checker: POST → 202 { report } (409 running, 429 too soon); GET → { report | null }.
  kbLinkCheck: () => req('POST', '/api/kb/links/check'),
  kbLinkReport: () => req('GET', '/api/kb/links/report'),
  // WordPress importer (docs/kb-specs/WP-IMPORT-SPEC.md §3; KB_IMPORT_ACCESS — owner — while the
  // server has it on: capabilities.kbImport). detect → { site, page, categories }; items (pasted
  // addresses / ids) → { site, items }; create b = { url, scope, exclude?, options?, replace? } →
  // 202 { job }; jobs → { jobs, budget:{ previews, replans } }; job → { job } (poll while
  // planning/running); patch exclude (leaf articles only — no site request) → { job } | 409
  // replan_required; run b = { publish?, createRedirects? }; publish → { published, failed,
  // skipped, total, publishedIds }.
  kbImportDetect: (url) => req('POST', '/api/kb/import/wp/detect', { url }),
  kbImportLookup: (url, items) => req('POST', '/api/kb/import/wp/items', { url, items }),
  kbImportCreate: (b) => req('POST', '/api/kb/import/wp/jobs', b),
  kbImportJobs: () => req('GET', '/api/kb/import/jobs'),
  kbImportJob: (id) => req('GET', `/api/kb/import/jobs/${id}`),
  kbImportPatchExclude: (id, exclude) => req('PATCH', `/api/kb/import/jobs/${id}`, { exclude }),
  kbImportRun: (id, b = {}) => req('POST', `/api/kb/import/jobs/${id}/run`, b),
  kbImportCancel: (id) => req('POST', `/api/kb/import/jobs/${id}/cancel`),
  kbImportPublish: (id) => req('POST', `/api/kb/import/jobs/${id}/publish`),
  kbImportRedirectsUrl: (id, format) =>
    `/api/kb/import/jobs/${id}/redirects.${format === 'plain' || format === 'redirection' ? 'csv' : 'txt'}?format=${format}`,
  // Insights (days ∈ 30 | 90 | 180) → { days, searchTerms, feedback }; delete = owner.
  kbInsights: (days = 30) => req('GET', `/api/kb/insights?days=${days}`),
  kbDeleteSearchTerms: () => req('DELETE', '/api/kb/insights/search-terms'),
  // Multipart upload of one image/file into an article → { attachment: { id, name, size,
  // ext, isImage, scanState } }. Errors like uploadAttachment's, plus err.status and
  // err.data (e.g. data.reason === 'uploads_disabled', 413 = too large).
  kbUploadMedia: async (articleId, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    const res = await fetch(`/api/kb/media?articleId=${encodeURIComponent(articleId)}`, {
      method: 'POST', credentials: 'same-origin', body: fd
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      if (res.status === 401 && onUnauthorized) { try { onUnauthorized(); } catch { /* ignore */ } }
      const err = new Error(data?.message || `Upload failed (${res.status})`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  },
  // In-editor preview of an upload (agent-authed; 302 → presigned URL).
  kbMediaPreviewUrl: (id) => `/api/attachments/${id}`,
  // MCP connector (any agent) — consent page + Settings → Profile → Connect your AI
  // (docs/kb-specs/P3-SPEC.md §4.4, §7.4, §7.5; MCP-TICKETS-SPEC.md). The consent POST answers
  // { redirectUrl } (built server-side; the page just location.assign()s it); a step-up miss is
  // a 401 reason 'reauth_required' / 403 reason 'fresh_signin_required' in err.data, and an
  // agent the owner hasn't allowed gets 403 reason 'agents_not_allowed'.
  oauthConsent: (id) => req('GET', `/api/oauth/consent/${encodeURIComponent(id)}`),
  oauthConsentDecide: (id, b) => req('POST', `/api/oauth/consent/${encodeURIComponent(id)}`, b), // { approve, scopes?, assertion? | code? }
  // The signed-in agent's own connected apps; `all` (owner only) = every app in the workspace.
  oauthGrants: (all = false) => req('GET', `/api/oauth/grants${all ? '?scope=workspace' : ''}`),
  // Owner: "Agents can connect AI apps" (off disconnects every agent's app) → { agentAccess, disconnected }.
  mcpSettings: () => req('GET', '/api/admin/mcp-settings'),
  putMcpSettings: (b) => req('PUT', '/api/admin/mcp-settings', b),
  oauthRevokeGrant: (id) => req('DELETE', `/api/oauth/grants/${encodeURIComponent(id)}`),
  oauthGrantActivity: (id, cursor) =>
    req('GET', `/api/oauth/grants/${encodeURIComponent(id)}/activity${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  oauthGrantRevertAll: (id, b) => req('POST', `/api/oauth/grants/${encodeURIComponent(id)}/revert`, b), // { since, dryRun }
  // Object storage (Images & Files): B2 / R2 / S3 + active-provider selector.
  adminStorage: () => req('GET', '/api/admin/storage'),
  adminPutStorage: (body) => req('PUT', '/api/admin/storage', body),
  adminStorageBuckets: (provider) => req('GET', `/api/admin/storage/${provider}/buckets`),
  adminStorageTest: (provider) => req('POST', `/api/admin/storage/${provider}/test`),
  adminStorageMigrate: (target) => req('POST', '/api/admin/storage/migrate', { target }),
  adminApiKeys: () => req('GET', '/api/admin/api-keys'),
  adminCreateApiKey: (label) => req('POST', '/api/admin/api-keys', { label }),
  adminRevokeApiKey: (id) => req('DELETE', `/api/admin/api-keys/${id}`),
  // SSO signing secrets — sign the JWTs that log customers in from a host app
  // (the Contact Support link). Rotate returns the raw secret exactly once; retire
  // schedules the secret to stop verifying after a grace window (default 24h).
  adminSigningSecrets: () => req('GET', '/api/admin/signing-secrets'),
  adminRotateSigningSecret: () => req('POST', '/api/admin/signing-secrets'),
  adminRetireSigningSecret: (id) => req('POST', `/api/admin/signing-secrets/${id}/retire`, {}),

  // Webhooks (owner)
  adminWebhooks: () => req('GET', '/api/admin/webhooks'),
  adminCreateWebhook: (body) => req('POST', '/api/admin/webhooks', body),
  adminUpdateWebhook: (id, body) => req('PATCH', `/api/admin/webhooks/${id}`, body),
  adminDeleteWebhook: (id) => req('DELETE', `/api/admin/webhooks/${id}`),
  adminRotateWebhookSecret: (id) => req('POST', `/api/admin/webhooks/${id}/rotate-secret`),
  adminTestWebhook: (id) => req('POST', `/api/admin/webhooks/${id}/test`),
  adminWebhookDeliveries: (id) => req('GET', `/api/admin/webhooks/${id}/deliveries`),

  // Embeddable widget (owner)
  adminWidget: () => req('GET', '/api/admin/widget'),
  adminPutWidget: (body) => req('PUT', '/api/admin/widget', body),
  adminCustomerAccounts: () => req('GET', '/api/admin/customer-accounts'),
  adminPutCustomerAccounts: (body) => req('PUT', '/api/admin/customer-accounts', body),

  // Outbound email — bring-your-own provider (owner)
  adminEmailSending: () => req('GET', '/api/admin/email-sending'),
  adminPutEmailSending: (body) => req('PUT', '/api/admin/email-sending', body),
  adminTestEmailSending: (to) => req('POST', '/api/admin/email-sending/test', { to }),

  // Inbound email (owner)
  adminInboundEmail: () => req('GET', '/api/admin/inbound-email'),
  adminPutInboundEmail: (body) => req('PUT', '/api/admin/inbound-email', body),
  adminRotateInboundEmail: () => req('POST', '/api/admin/inbound-email/rotate'),
  adminInboundEmailLog: () => req('GET', '/api/admin/inbound-email/log'),

  // Linear integration (owner card + agent create-issue). `connect` is a full-page
  // 302 to Linear, so the component navigates window.location there — it isn't a fetch.
  adminLinear: () => req('GET', '/api/admin/linear'),
  adminPutLinear: (body) => req('PUT', '/api/admin/linear', body),
  adminLinearDisconnect: () => req('POST', '/api/admin/linear/disconnect', {}),
  adminLinearTest: () => req('POST', '/api/admin/linear/test', {}),
  linearTeams: () => req('GET', '/api/linear/teams'),
  linearLabels: () => req('GET', '/api/linear/labels'),
  createLinearIssue: (ticketId, body) => req('POST', `/api/tickets/${ticketId}/linear-issue`, body || {}),
  unlinkLinearIssue: (ticketId) => req('DELETE', `/api/tickets/${ticketId}/linear-issue`),

  // Custom domains (owner)
  adminCustomHosts: () => req('GET', '/api/admin/custom-hosts'),
  supportSso: (next) => req('POST', '/api/support/sso', next ? { next } : {}),
  // Sandbox playground — the "Open Sandbox" handoff runs on the tenant host; the rest
  // run on sandbox.<root> (same-origin cookie).
  sandboxOpen: () => req('POST', '/api/admin/sandbox/open', {}),
  sandboxContext: () => req('GET', '/api/sandbox/context'),
  sandboxWebhooks: () => req('GET', '/api/sandbox/webhooks'),
  sandboxCreateWebhook: (body) => req('POST', '/api/sandbox/webhooks', body),
  sandboxRemoveWebhook: (id) => req('DELETE', `/api/sandbox/webhooks/${id}`),
  sandboxTestWebhook: (id) => req('POST', `/api/sandbox/webhooks/${id}/test`, {}),
  sandboxWebhookDeliveries: (id) => req('GET', `/api/sandbox/webhooks/${id}/deliveries`),
  sandboxVerifyWebhookSignature: (body) => req('POST', '/api/sandbox/validators/webhook-signature', body),
  sandboxApiKeys: () => req('GET', '/api/sandbox/api-keys'),
  sandboxCreateApiKey: (label) => req('POST', '/api/sandbox/api-keys', { label }),
  sandboxRevokeApiKey: (id) => req('DELETE', `/api/sandbox/api-keys/${id}`),
  sandboxInboundEmail: () => req('GET', '/api/sandbox/inbound-email'),
  sandboxPutInboundEmail: (body) => req('PUT', '/api/sandbox/inbound-email', body),
  sandboxRotateInboundEmail: () => req('POST', '/api/sandbox/inbound-email/rotate', {}),
  sandboxWidget: () => req('GET', '/api/sandbox/widget'),
  sandboxPutWidget: (body) => req('PUT', '/api/sandbox/widget', body),
  sandboxSsoSample: (body) => req('POST', '/api/sandbox/sso/sample', body || {}),
  sandboxValidateSso: (body) => req('POST', '/api/sandbox/validators/sso', body),
  sandboxValidateDomain: (host) => req('POST', '/api/sandbox/validators/domain', { host }),
  sandboxEmailTest: (to) => req('POST', '/api/sandbox/validators/email-test', { to }),
  sandboxAccountLink: () => req('POST', '/api/sandbox/validators/account-link', {}),
  sandboxReset: () => req('POST', '/api/sandbox/reset', {}),
  adminSubdomainAvailable: (slug) => req('GET', `/api/admin/subdomain/available?slug=${encodeURIComponent(slug)}`),
  adminChangeSubdomain: (slug) => req('PUT', '/api/admin/subdomain', { slug }),
  adminAddCustomHost: (host) => req('POST', '/api/admin/custom-hosts', { host }),
  adminVerifyCustomHost: (host) => req('POST', `/api/admin/custom-hosts/${encodeURIComponent(host)}/verify`),
  adminRemoveCustomHost: (host) => req('DELETE', `/api/admin/custom-hosts/${encodeURIComponent(host)}`),
  adminSetCanonical: (host) => req('PUT', '/api/admin/canonical-host', { host }),

  // Billing (owner only — Stripe Team plan). Every call 503s with
  // billingCode:'BILLING_NOT_CONFIGURED' until the owner wires Stripe keys; the UI
  // reads that as its "billing not configured" disabled state (feature ships dark).
  // billingCheckout returns { clientSecret } for the embedded-checkout modal;
  // billingPortal returns { url } to redirect to Stripe's Customer Portal; seats/plan
  // return the same { plan, seats, activeAgents, billingConfigured } snapshot as GET.
  // `returnPath` (checkout + portal): the KB page Billing was opened on (the KB header
  // island — docs/KB.md gotcha 27), so Stripe sends the owner back THERE; the server honours
  // only a canonical KB page path and builds the URL on the workspace's own host. Omitted
  // (the ticket app) → back to /tickets/.
  billing: () => req('GET', '/api/admin/billing'),
  billingCheckout: (plan, returnPath) => req('POST', '/api/admin/billing/checkout', returnPath ? { plan, returnPath } : { plan }),
  // Link a just-completed checkout's sub synchronously on return (no webhook wait);
  // returns the same snapshot as GET. Idempotent server-side.
  billingReconcile: (sessionId) => req('POST', '/api/admin/billing/reconcile', { sessionId }),
  billingPortal: (returnPath) => req('POST', '/api/admin/billing/portal', returnPath ? { returnPath } : undefined),
  billingSeats: (seats) => req('POST', '/api/admin/billing/seats', { seats }),
  billingPlan: (plan) => req('POST', '/api/admin/billing/plan', { plan })
};
